/**
 * Manual start: hand-typed design points for an exploration's first
 * population (backend: colorExplorer/manual_start.py).
 *
 * Rows are kept as strings (what the user typed); every cell is validated
 * against the parameter's value grid and hardware limits. Off-grid values
 * are refused and the nearest allowed value is shown; nothing is snapped
 * unless the user presses "Snap all to grid".
 */

export const INTEGER_PARAMETERS = ["pass_count", "line_count"];

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

/** Ordered specs: the laser parameters first, then the cluster parameters. */
export function manualSpecsFromConfigs(explorationConfig, laserConfig) {
  const laser = laserConfig?.laser_design_space_parameters;
  const cluster = explorationConfig?.optimization?.cluster_design_space_parameters;
  const entries = [
    ...Object.entries(laser && typeof laser === "object" ? laser : {}),
    ...Object.entries(cluster && typeof cluster === "object" ? cluster : {}),
  ];
  return entries
    .map(([name, raw]) => {
      const integer = INTEGER_PARAMETERS.includes(name);
      const values = [...new Set((raw?.values || []).filter(isNumber))].sort((a, b) => a - b);
      if (values.length === 0) return null;
      return {
        name,
        values,
        limitMin: isNumber(raw?.limit_min) ? raw.limit_min : values[0],
        limitMax: isNumber(raw?.limit_max) ? raw.limit_max : values[values.length - 1],
        defaultValue: isNumber(raw?.default_value) ? raw.default_value : values[0],
        optimize: raw?.optimize !== false,
        integer,
      };
    })
    .filter(Boolean);
}

/** The grid value closest to `value`; on a tie the lower one. */
export function nearestGridValue(value, values) {
  let best = values[0];
  let bestDistance = Math.abs(value - best);
  for (const candidate of values.slice(1)) {
    const distance = Math.abs(value - candidate);
    if (distance < bestDistance && Math.abs(distance - bestDistance) > 1e-12) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

/** The grid's own element equal to `value`, or null when off the grid. */
export function gridElement(value, values) {
  for (const candidate of values) {
    if (Math.abs(value - candidate) <= 1e-9 * Math.max(1, Math.abs(value), Math.abs(candidate))) {
      return candidate;
    }
  }
  return null;
}

/**
 * Validate one typed cell. Result: `{status, value, nearest}` with status
 * one of empty | nan | integer | limits | offgrid | ok.
 */
export function validateCell(text, spec) {
  const trimmed = String(text ?? "").trim();
  if (trimmed === "") return { status: "empty", value: null, nearest: null };
  const value = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(value)) return { status: "nan", value: null, nearest: null };
  if (spec.integer && !Number.isInteger(value)) return { status: "integer", value, nearest: null };
  if (value < spec.limitMin || value > spec.limitMax) return { status: "limits", value, nearest: null };
  const element = gridElement(value, spec.values);
  if (element === null) return { status: "offgrid", value, nearest: nearestGridValue(value, spec.values) };
  return { status: "ok", value: element, nearest: null };
}

/** Caption under an invalid cell; null for a valid one. */
export function cellMessage(cell, spec) {
  switch (cell?.status) {
    case "empty":
      return "required";
    case "nan":
      return "not a number";
    case "integer":
      return "integer";
    case "limits":
      return `${spec.limitMin} – ${spec.limitMax}`;
    case "offgrid":
      return `not on grid → ${cell.nearest}`;
    default:
      return null;
  }
}

const rowKey = (cells, specs) => specs.map((spec) => cells[spec.name]?.value).join("|");

/**
 * Validate every row. Result: `cells[row][name]`, `duplicateOf[row]` (index
 * of the earlier identical row or null), `offGridCount`, `invalidCount`,
 * `allValid` (every cell ok and no duplicates).
 */
export function validateRows(rows, specs) {
  const cells = rows.map((row) =>
    Object.fromEntries(specs.map((spec) => [spec.name, validateCell(row?.[spec.name], spec)]))
  );
  let offGridCount = 0;
  let invalidCount = 0;
  const seen = new Map();
  const duplicateOf = cells.map((rowCells, index) => {
    let rowValid = true;
    for (const spec of specs) {
      const status = rowCells[spec.name].status;
      if (status === "offgrid") offGridCount += 1;
      if (status !== "ok") {
        invalidCount += 1;
        rowValid = false;
      }
    }
    if (!rowValid) return null;
    const key = rowKey(rowCells, specs);
    if (seen.has(key)) return seen.get(key);
    seen.set(key, index);
    return null;
  });
  const duplicates = duplicateOf.filter((d) => d !== null).length;
  return { cells, duplicateOf, offGridCount, invalidCount, allValid: invalidCount === 0 && duplicates === 0 };
}

/** Replace only the off-grid cells by the nearest grid value shown to the user. */
export function snapRowsToGrid(rows, specs) {
  return rows.map((row) => {
    const next = { ...row };
    for (const spec of specs) {
      const cell = validateCell(row?.[spec.name], spec);
      if (cell.status === "offgrid") next[spec.name] = String(cell.nearest);
    }
    return next;
  });
}

/** A new row prefilled with each parameter's default value. */
export function defaultRow(specs) {
  return Object.fromEntries(specs.map((spec) => [spec.name, String(spec.defaultValue)]));
}

/**
 * Rows pasted from a spreadsheet: one line per sample, cells separated by
 * tabs, semicolons or commas, in the column order of `specs`. Blank lines
 * are skipped; extra cells are dropped; missing cells stay empty.
 */
export function parsePastedRows(text, specs) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      // Tabs or semicolons separate cells (a comma is then a decimal comma);
      // without them, commas do.
      const separator = /[\t;]/.test(line) ? /\t|;/ : /,/;
      const parts = line.split(separator).map((part) => part.trim());
      return Object.fromEntries(specs.map((spec, index) => [spec.name, parts[index] ?? ""]));
    });
}

/** The typed rows as numbers, in the column order of `specs` (valid rows only). */
export function rowsToPoints(rows, specs) {
  return rows.map((row) =>
    Object.fromEntries(specs.map((spec) => [spec.name, validateCell(row?.[spec.name], spec).value]))
  );
}

export function fillSummary(typedCount, populationSize) {
  const filled = Math.max(0, populationSize - typedCount);
  return `${typedCount} typed + ${filled} filled = ${populationSize} samples`;
}

/** Whether the manual start can be sent; `reason` names the first problem. */
export function manualStartReady({ rows, specs, vary, populationSize, gridSize }) {
  if (!specs || specs.length === 0) return { ok: false, reason: "The design space is not loaded yet." };
  if (!Number.isInteger(populationSize) || populationSize <= 0) {
    return { ok: false, reason: "The population size is not set in the exploration configuration." };
  }
  const perBatch = Array.isArray(gridSize) && gridSize.length === 2 ? Number(gridSize[0]) * Number(gridSize[1]) : null;
  if (perBatch && populationSize % perBatch !== 0) {
    return {
      ok: false,
      reason: `The population size (${populationSize}) must be a multiple of the patch group grid (${gridSize[0]} × ${gridSize[1]} = ${perBatch}).`,
    };
  }
  if (!rows || rows.length === 0) return { ok: false, reason: "Type at least one sample." };
  if (rows.length > populationSize) {
    return { ok: false, reason: `${rows.length} samples are typed but the population size is ${populationSize}.` };
  }
  const validation = validateRows(rows, specs);
  if (validation.invalidCount > 0) return { ok: false, reason: "Some values are not usable; see the table." };
  const duplicate = validation.duplicateOf.findIndex((d) => d !== null);
  if (duplicate >= 0) {
    return { ok: false, reason: `Samples ${validation.duplicateOf[duplicate] + 1} and ${duplicate + 1} are identical.` };
  }
  if (rows.length < populationSize && (!vary || vary.length === 0)) {
    return { ok: false, reason: "Pick at least one parameter to vary for the filler samples." };
  }
  return { ok: true, reason: null };
}
